import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** Nav sequence for horizontal swipe between mobile bottom tabs.
 * Keep this in sync with `MobileBottomNav.NAV_ITEMS`.
 * Feb 2026: Videos removed from the bottom nav (already accessible via
 * the Picture Library switcher). Transcripts sits in its place. */
const NAV_SEQUENCE = ['/', '/cc-picture', '/topics', '/transcripts', '/more'];

/** Bind edge-to-edge horizontal swipes on mobile to move forward/back
 * through the bottom-nav tabs — like Instagram feeds & iOS home screens.
 *
 * Requirements:
 *   - viewport <= 640px (mobile only)
 *   - swipe must be dominantly horizontal (2× more horizontal than vertical)
 *   - swipe distance > 60px
 *   - ignore swipes that start on interactive elements (button, input, slider)
 */
export function useMobileTabSwipe() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(min-width: 640px)').matches) return;

    let startX = 0, startY = 0, tracking = false, multiTouch = false;

    const isInteractive = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      if (['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT', 'A', 'VIDEO', 'AUDIO'].includes(tag)) return true;
      const role = el.getAttribute?.('role');
      if (role && ['slider', 'button', 'switch', 'combobox', 'checkbox', 'menuitem'].includes(role)) return true;
      // Feb 2026 (user "sometimes when i press on a speaker it changes
      // the url but doesnt enter him"): SpeakerCard tiles are plain
      // <div>s, so a tap with any finger tremor >60px was being
      // interpreted as a horizontal swipe → nav went to the next
      // bottom-nav tab instead of the speaker's page. Bail out of the
      // swipe handler on ANY tile that has a `speaker-card-*` testid
      // or on any element carrying `data-no-swipe`. This treats tiles
      // as interactive so their onClick fires cleanly.
      if (el.closest?.('[data-testid^="speaker-card-"]')) return true;
      if (el.closest?.('[data-testid^="recording-card-"]')) return true;
      if (el.closest?.('[data-testid^="folder-card-"]')) return true;
      // Any ancestor sliders, image carousels, etc.
      // react-easy-crop emits `reactEasyCrop_Container` / `reactEasyCrop_Image`
      // (camelCase, no dashes) — the old `[class*="react-easy-crop"]` match
      // never hit, so pinching the canvas-maker cropper fell through to the
      // swipe handler below and navigated the user off the page.
      if (el.closest?.('[role="slider"], [role="dialog"], .no-swipe, [data-no-swipe], [class*="reactEasyCrop"]')) return true;
      return false;
    };

    const onStart = (e) => {
      // Jun-2026 (user: "pinching on iphone breaks the app … reloads or
      // crashes it, e.g. zooming a pic in canvas maker"): a pinch ends with
      // one finger travelling well past the 60px threshold, so touchend fired
      // a tab navigation — the open modal unmounted mid-gesture and the app
      // looked like it had crashed/reloaded. Any second finger now cancels
      // the swipe outright.
      if (e.touches && e.touches.length > 1) { multiTouch = true; tracking = false; return; }
      const t = e.touches ? e.touches[0] : e;
      if (!t) return;
      if (isInteractive(e.target)) { tracking = false; return; }
      startX = t.clientX; startY = t.clientY;
      tracking = true;
    };
    const onMove = (e) => {
      if (e.touches && e.touches.length > 1) { multiTouch = true; tracking = false; }
    };
    const onEnd = (e) => {
      // Fingers still down (mid-pinch release) — never navigate.
      const remaining = e.touches ? e.touches.length : 0;
      if (remaining > 0) { tracking = false; multiTouch = true; return; }
      const wasMultiTouch = multiTouch;
      multiTouch = false;
      if (wasMultiTouch) { tracking = false; return; }
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches ? e.changedTouches[0] : e;
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (Math.abs(dx) < 60) return;
      if (Math.abs(dx) < Math.abs(dy) * 2) return;   // must be dominantly horizontal
      // Find our current index in the sequence; walk forward/back.
      const idx = NAV_SEQUENCE.findIndex((p) => (
        p === '/' ? location.pathname === '/' : location.pathname.startsWith(p)
      ));
      if (idx < 0) return;
      const nextIdx = dx < 0
        ? Math.min(NAV_SEQUENCE.length - 1, idx + 1)   // swipe left → next tab
        : Math.max(0, idx - 1);                        // swipe right → prev tab
      if (nextIdx !== idx) navigate(NAV_SEQUENCE[nextIdx]);
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [location.pathname, navigate]);
}
